import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Brain, FileCog, LayoutGrid, Menu, Palette, Settings } from 'lucide-react';
import { hostHas } from '../shared/host-capabilities';
import { WorkbenchPersonSection, workbenchPerson } from './WorkbenchPerson';
import './workbench-navigation.css';

/** Current workspace's existing suggestion snapshot; never initiates a mining request. */
export const LearningPendingContext = createContext(0);
export const LearningImprovementActionContext = createContext<(() => void) | null>(null);

export interface WorkbenchNavigationProps {
  currentJob?: string;
  documentCount: number | null;
  compactCount: string;
  documentsLocked: boolean;
  documentsLabel: string;
  settingsLabel: string;
  cudaActive: boolean;
  acceleratorSays: string | null;
  updateReady: boolean;
  activationSays: string | null;
  clusterAsks: boolean;
  learningLive: boolean;
  onJobs: () => void;
  onCapture: () => void;
  onSetup: () => void;
  onExport: () => void;
  onSettings: () => void;
  onCluster: () => void;
  onLearning: () => void;
  onAppearance?: () => void;
}

/** One inline presentation of Sidebar's existing route and status owners. */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function WorkbenchNavigation(props: WorkbenchNavigationProps) {
  const pendingImprovements = useContext(LearningPendingContext);
  const openImprovements = useContext(LearningImprovementActionContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const owner = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const keyboardOpening = useRef(false);
  const menuId = useId();
  const focusTrigger = () => trigger.current?.focus({ preventScroll: true });
  const close = () => {
    const focusInside = menu.current?.contains(document.activeElement);
    setMenuOpen(false);
    if (focusInside) focusTrigger();
  };
  const navigate = (action: () => void) => {
    close();
    action();
  };
  const attention = Boolean(
    props.activationSays || props.acceleratorSays || props.clusterAsks || pendingImprovements > 0,
  );
  const statuses = [
    props.activationSays,
    props.acceleratorSays,
    props.updateReady ? 'an update is ready to install' : null,
    props.clusterAsks ? 'a computer is asking to join this group' : null,
    props.learningLive ? 'dedicated learning is running' : null,
    pendingImprovements > 0
      ? `${pendingImprovements} suggested job ${pendingImprovements === 1 ? 'adjustment' : 'adjustments'}`
      : null,
  ].filter(Boolean);
  const triggerLabel = `Workspace menu${statuses.length ? ` - ${statuses.join('; ')}` : ''}`;
  const person = workbenchPerson();

  useEffect(() => {
    if (!menuOpen) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !owner.current?.contains(event.target))
        setMenuOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    window.addEventListener('pointerdown', outside);
    window.addEventListener('keydown', escape, true);
    return () => {
      window.removeEventListener('pointerdown', outside);
      window.removeEventListener('keydown', escape, true);
    };
  }, [menuOpen]);
  useLayoutEffect(() => {
    if (!menuOpen) return;
    // Opening a read-only menu with the pointer must not blur/save an editor.
    if (keyboardOpening.current)
      menu.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const position = () => {
      const popup = menu.current,
        anchor = trigger.current,
        element = owner.current;
      if (!popup || !anchor || !element) return;
      const bounds = element.getBoundingClientRect(),
        anchorBox = anchor.getBoundingClientRect();
      const maximumLeft = Math.max(8, window.innerWidth - popup.getBoundingClientRect().width - 8);
      popup.style.left = `${Math.min(Math.max(8, anchorBox.left), maximumLeft) - bounds.left}px`;
      popup.style.maxHeight = `${Math.max(80, window.innerHeight - anchorBox.bottom - 14)}px`;
    };
    position();
    const observer = new ResizeObserver(position);
    if (owner.current)
      observer.observe(owner.current.closest('.workbench-review-toolbar') ?? owner.current);
    window.addEventListener('resize', position);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', position);
    };
  }, [menuOpen]);
  const menuKeyboard = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Document navigation shortcuts never act through the open keyboard menu.
    event.stopPropagation();
    const items = [
      ...(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []),
    ];
    if (!items.length) return;
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    let next: number;
    if (event.key === 'ArrowDown') next = (index + 1) % items.length;
    else if (event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else if (event.key === 'Tab') {
      event.preventDefault();
      const buttons = [
        ...(owner.current
          ?.closest('.workbench-review-toolbar')
          ?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []),
      ].filter((button) => button.getClientRects().length > 0 && !menu.current?.contains(button));
      const after = buttons[buttons.indexOf(trigger.current!) + 1];
      close();
      if (!event.shiftKey) after?.focus({ preventScroll: true });
      return;
    } else return;
    event.preventDefault();
    items[next]?.focus();
  };
  return (
    <div className="workbench-navigation" ref={owner}>
      <button
        ref={trigger}
        type="button"
        className="workbench-menu-trigger"
        aria-label={triggerLabel}
        title={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-controls={menuId}
        onMouseDown={(event) => event.preventDefault()}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            keyboardOpening.current = true;
            if (menuOpen)
              menu.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
            else setMenuOpen(true);
          }
        }}
        onClick={(event) => {
          if (menuOpen) close();
          else {
            keyboardOpening.current = event.detail === 0;
            setMenuOpen(true);
          }
        }}
      >
        <Menu size={19} />
        {statuses.length > 0 && (
          <span
            className={`workbench-status-dot${attention ? ' attention' : ''}`}
            aria-hidden="true"
          />
        )}
      </button>
      {menuOpen && (
        <div
          id={menuId}
          ref={menu}
          role="menu"
          aria-label="Workspace navigation"
          className="workbench-menu"
          onKeyDown={menuKeyboard}
          onKeyUp={(event) => event.stopPropagation()}
        >
          {window.paigeWorkbench && (
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              onClick={() => navigate(window.paigeWorkbench!.leave)}
            >
              <LayoutGrid size={22} />
              <span>Dashboard</span>
            </button>
          )}
          {props.currentJob && (
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              onClick={() => navigate(props.onSetup)}
            >
              <FileCog size={22} />
              <span>Job setup</span>
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={() => navigate(props.onAppearance ?? props.onSettings)}
          >
            <Palette size={22} />
            <span>Appearance</span>
          </button>
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={() =>
              navigate(
                pendingImprovements > 0 && openImprovements ? openImprovements : props.onLearning,
              )
            }
          >
            <Brain size={22} />
            <span>Learning</span>
            {pendingImprovements > 0 ? (
              <small>
                {pendingImprovements} job {pendingImprovements === 1 ? 'adjustment' : 'adjustments'}
              </small>
            ) : (
              props.learningLive && <small>Running</small>
            )}
          </button>
          {hostHas('workbench-settings') && (
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              onClick={() => navigate(props.onSettings)}
            >
              <Settings size={22} />
              <span>Settings</span>
              {props.updateReady && <small>Update</small>}
            </button>
          )}
          {person && <WorkbenchPersonSection person={person} onChoose={navigate} />}
        </div>
      )}
    </div>
  );
}
